
export const createEmptyDoc = () => {
    return {
        type: "doc",
        content: [
            {
                type: "paragraph",
                content: []
            }
        ]
    };
}

export const convertHTMLToBaseModel = ({ html }) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    function parseNode(node) {
        // TEXT NODE
        if (node.nodeType === Node.TEXT_NODE) {
            const content = node.textContent;
            // Keep text nodes even if they're whitespace-only
            // Only skip completely empty nodes
            if (content === "") return null;

            return {
                type: "text",
                content,
            };
        }

        // ELEMENT NODE
        if (node.nodeType === Node.ELEMENT_NODE) {
            const children = Array.from(node.childNodes)
                .map(parseNode)
                .filter(Boolean);

            // ✅ lấy attributes
            const attributes = {};
            for (const attr of node.attributes) {
                attributes[attr.name] = attr.value;
            }

            // For inline elements like span, font, ensure they have children
            // even if empty (to preserve structure)
            const isInlineElement = ['span', 'font', 'b', 'strong', 'i', 'em', 'u', 's', 'code', 'a'].includes(node.tagName.toLowerCase());

            // Block elements that should always have children array
            const isBlockElement = ['li', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'ul', 'ol'].includes(node.tagName.toLowerCase());

            const result = {
                type: node.tagName.toLowerCase(),
                ...(Object.keys(attributes).length && { attributes }),
            };

            // Always add children for inline elements and block elements
            if (isInlineElement || isBlockElement) {
                result.children = children;
            } else if (children.length > 0) {
                result.children = children;
            }

            return result;
        }

        return null;
    }

    return Array.from(doc.body.childNodes)
        .map(parseNode)
        .filter(Boolean);
};
export const htmlToText = ({ html }) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
}

export const convertBaseModelToHTML = ({ doc }) => {
    let nodes;

    if (Array.isArray(doc)) {
        nodes = doc;
    } else if (doc && doc.type === "doc") {
        nodes = doc.content || [];
    } else {
        return "";
    }
    const html = renderNodes({ nodes });
    return html;
}

export const renderNodes = ({ nodes }) => {
    if (!nodes || !Array.isArray(nodes)) return "";
    return nodes.filter(node => node && node.type).map(node => renderNode({ node })).join("");
}

export const buildAttributesString = ({ attributes }) => {
    if (!attributes || typeof attributes !== 'object') return "";
    return Object.entries(attributes)
        .map(([key, value]) => {
            if (key === 'style') return `style="${escapeHTML({ string: value })}"`;
            if (key === 'class') return `class="${escapeHTML({ string: value })}"`;
            return `${key}="${escapeHTML({ string: String(value) })}"`;
        })
        .join(" ");
}

export const renderNode = ({ node }) => {
    const attrs = node.attributes ? ` ${buildAttributesString({ attributes: node.attributes })}` : "";

    switch (node.type) {
        case "text":
            // Handle text nodes - render as inline
            return renderInlineNode({ node });

        case "paragraph":
        case "p":
            return `<p${attrs}>${renderInline({ nodes: node.children || node.content || [] })}</p>`;

        case "heading":
        case "h1":
        case "h2":
        case "h3":
        case "h4":
        case "h5":
        case "h6": {
            const level = node.level || parseInt(node.type.substring(1)) || 1;
            return `<h${level}${attrs}>${renderInline({ nodes: node.children || node.content || [] })}</h${level}>`;
        }

        case "blockquote":
            return `<blockquote${attrs}>${renderNodes({ nodes: node.children || node.content || [] })}</blockquote>`;

        case "bullet_list":
        case "ul":
            return `<ul${attrs}>${renderNodes({ nodes: node.children || node.content || [] })}</ul>`;

        case "ordered_list":
        case "ol":
            return `<ol${attrs}>${renderNodes({ nodes: node.children || node.content || [] })}</ol>`;

        case "list_item":
        case "li": {
            // li can contain both inline content (text, spans) and block content (nested lists)
            // Render children using renderNodes to handle both cases
            const children = node.children || node.content || [];
            const content = children.map(child => {
                // If it's a block element (ul, ol), use renderNode
                if (child.type === 'ul' || child.type === 'ol' || child.type === 'bullet_list' || child.type === 'ordered_list') {
                    return renderNode({ node: child });
                }
                // Otherwise render as inline
                return renderInlineNode({ node: child });
            }).join("");
            return `<li${attrs}>${content}</li>`;
        }
        case "a":
            return `<a${attrs}>${renderInline({ nodes: node.children || node.content || [] })}</a>`;
        default:
            return "";
    }
}

export const renderInline = ({ nodes }) => {
    if (!nodes || !Array.isArray(nodes)) return "";
    return nodes.filter(node => node && node.type).map(node => renderInlineNode({ node })).join("");
}

export const renderInlineNode = ({ node }) => {

    if (node.type === "text") {
        const text = node.text || node.content || "";
        return applyMarks({ text: escapeHTML({ string: text }), marks: node.marks });
    }

    if (node.type === "hard_break" || node.type === "br") {
        return "<br/>";
    }

    const attrs = node.attributes ? ` ${buildAttributesString({ attributes: node.attributes })}` : "";

    // Handle inline formatting tags (b, strong, i, em, u, s, code, span, a, etc.)
    if (node.type === "b" || node.type === "strong") {
        return `<strong${attrs}>${renderInline({ nodes: node.children || [] })}</strong>`;
    }

    if (node.type === "i" || node.type === "em") {
        return `<em${attrs}>${renderInline({ nodes: node.children || [] })}</em>`;
    }

    if (node.type === "u") {
        return `<u${attrs}>${renderInline({ nodes: node.children || [] })}</u>`;
    }

    if (node.type === "s" || node.type === "strike") {
        return `<s${attrs}>${renderInline({ nodes: node.children || [] })}</s>`;
    }

    if (node.type === "code") {
        return `<code${attrs}>${renderInline({ nodes: node.children || [] })}</code>`;
    }

    if (node.type === "a") {
        return `<a${attrs}>${renderInline({ nodes: node.children || [] })}</a>`;
    }

    if (node.type === "span") {
        // Span can have style in attributes or as a direct property
        let finalAttrs = "";
        if (node.attributes) {
            finalAttrs = ` ${buildAttributesString({ attributes: node.attributes })}`;
        } else if (node.style) {
            finalAttrs = ` style="${node.style}"`;
        }
        return `<span${finalAttrs}>${renderInline({ nodes: node.children || [] })}</span>`;
    }
    return "";
}

export const applyMarks = ({ text, marks }) => {
    if (!marks || marks.length === 0) return text;

    return marks.reduce((acc, mark) => {
        switch (mark.type) {
            case "bold":
                return `<strong>${acc}</strong>`;

            case "italic":
                return `<em>${acc}</em>`;

            case "underline":
                return `<u>${acc}</u>`;

            case "strike":
                return `<s>${acc}</s>`;

            case "code":
                return `<code>${acc}</code>`;

            case "color":
                return `<span style="color:${mark.attrs?.color}">${acc}</span>`;

            default:
                return acc;
        }
    }, text);
}

export const escapeHTML = ({ string }) => {
    return string
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

export const setCursorPosition = ({ doc }) => {
    // Handle empty or invalid doc
    if (!doc || !Array.isArray(doc) || doc.length === 0) {
        return null;
    }

    const lastBlockIndex = doc.length - 1;
    const lastBlock = doc[lastBlockIndex];

    // Check if lastBlock has children
    if (!lastBlock || !lastBlock.children || lastBlock.children.length === 0) {
        return null;
    }

    const lastTextIndex = lastBlock.children.length - 1;
    const lastText = lastBlock.children[lastTextIndex];

    // Check if lastText has content
    if (!lastText || !lastText.content) {
        return null;
    }

    const textLength = lastText.content.length;

    const selection = {
        anchor: JSON.stringify({
            path: [lastBlockIndex, lastTextIndex],
            offset: textLength
        }),
        focus: JSON.stringify({
            path: [lastBlockIndex, lastTextIndex],
            offset: textLength
        })
    };

    return selection;
}